import { useEffect, useState } from "react";
import { api, formatApiError, dateOnly } from "@/lib/api";
import { UnitStatusBadge } from "@/components/StatusBadge";
import { Plus, Truck, X, Trash2, Edit3, KeyRound, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import HelpTip from "@/components/HelpTip";

const EMPTY_FORM = {
    economic_number: "", plates: "", brand: "", model: "", year: 2024, ambulance_type: "basica",
    vin: "", insurance_policy: "", insurance_expires: "", circulation_card_expires: "",
    verification_expires: "", base: "", current_km: 0, status: "disponible",
    current_paramedic_id: "", current_driver_id: "", current_medic_id: "",
    notes: "",
};

export default function Units() {
    const [list, setList] = useState([]);
    const [personnel, setPersonnel] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const load = async () => {
        try {
            const [u, p] = await Promise.all([api.get("/units"), api.get("/personnel")]);
            if (u?.data) setList(u.data);
            if (p?.data) setPersonnel(p.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
    const openEdit = (u) => {
        setEditingId(u.id);
        setForm({ ...EMPTY_FORM, ...u });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            // limpiar IDs vacíos
            ["current_paramedic_id", "current_driver_id", "current_medic_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
            if (editingId) {
                await api.put(`/units/${editingId}`, payload);
                toast.success("Unidad actualizada");
            } else {
                await api.post("/units", payload);
                toast.success("Unidad creada");
            }
            setOpen(false); setEditingId(null); setForm(EMPTY_FORM);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`¿Eliminar unidad "${u.economic_number}"?\n\nSe registra en Auditoría. Si la unidad tiene historial de servicios, mejor cambia su estatus a "fuera_servicio".`)) return;
        try { await api.delete(`/units/${u.id}`); toast.success("Eliminada"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const personById = (id) => personnel.find(p => p.id === id);
    const paramedics = personnel.filter(p => ["paramedico", "medico", "enfermera"].includes(p.position) && p.status === "activo");
    const drivers = personnel.filter(p => p.position === "operador" && p.status === "activo");
    const medics = personnel.filter(p => ["medico", "enfermera"].includes(p.position) && p.status === "activo");

    return (
        <div className="space-y-4" data-testid="units-page">
            <div className="flex justify-between items-center flex-wrap gap-3">
                <div className="text-sm text-slate-500">
                    {list.length} unidad(es) en flota · {list.filter(u => u.status === "disponible").length} disponibles
                </div>
                <button onClick={openNew} className="fm-btn-accent" data-testid="new-unit-btn">
                    <Plus className="w-3.5 h-3.5" /> Nueva unidad
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((u) => {
                    const para = personById(u.current_paramedic_id);
                    const drv = personById(u.current_driver_id);
                    const med = personById(u.current_medic_id);
                    return (
                        <div key={u.id} className="fm-card p-4 hover:shadow-md transition-shadow" data-testid={`unit-card-${u.economic_number}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Económico</div>
                                    <div className="font-mono font-black text-xl">{u.economic_number}</div>
                                </div>
                                <Truck className="w-6 h-6 text-slate-300" strokeWidth={1.5} />
                            </div>
                            <div className="space-y-1 text-xs">
                                <div className="flex justify-between"><span className="text-slate-500">Vehículo</span><span>{u.brand} {u.model} {u.year}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Placas</span><span className="font-mono">{u.plates}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Tipo</span><span className="capitalize">{u.ambulance_type}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Base</span><span>{u.base || "—"}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Km</span><span className="font-mono">{u.current_km?.toLocaleString() || 0}</span></div>
                                {u.insurance_expires && (
                                    <div className="flex justify-between"><span className="text-slate-500">Póliza vence</span><span className="font-mono">{dateOnly(u.insurance_expires)}</span></div>
                                )}
                            </div>

                            {/* Tripulación a bordo */}
                            <div className="mt-3 pt-3 border-t border-slate-100">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 flex items-center gap-1">
                                    <UserCheck className="w-3 h-3" /> Tripulación
                                </div>
                                <div className="space-y-0.5 text-[11px]">
                                    {para && <div className="flex justify-between"><span className="text-slate-500">Paramédico</span><span>{para.name}</span></div>}
                                    {drv && <div className="flex justify-between"><span className="text-slate-500">Operador</span><span>{drv.name}</span></div>}
                                    {med && <div className="flex justify-between"><span className="text-slate-500">Médico</span><span>{med.name}</span></div>}
                                    {!para && !drv && !med && <div className="text-slate-400 italic">Sin asignar</div>}
                                </div>
                            </div>

                            {/* Acceso a tablet */}
                            <div className="mt-3 pt-3 border-t border-slate-100">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 flex items-center gap-1">
                                    <KeyRound className="w-3 h-3" /> Acceso tablet
                                </div>
                                {u.login_email ? (
                                    <div className="text-[11px] font-mono text-emerald-700">{u.login_email}</div>
                                ) : (
                                    <Link to="/users" className="text-[11px] text-red-600 hover:underline">Sin acceso · crear en Accesos</Link>
                                )}
                            </div>

                            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                <UnitStatusBadge status={u.status} />
                                <div className="flex items-center gap-3 text-xs">
                                    <button onClick={() => openEdit(u)} className="text-slate-600 hover:text-red-600 inline-flex items-center gap-1" data-testid={`edit-unit-${u.id}`}><Edit3 className="w-3.5 h-3.5" /> Editar</button>
                                    <button onClick={() => remove(u)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1" data-testid={`delete-unit-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {list.length === 0 && (
                <div className="fm-card p-10 text-center text-sm text-slate-500">
                    <Truck className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                    Sin unidades en la flota.
                </div>
            )}

            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-3xl w-full max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="font-display font-bold text-lg">{editingId ? "Editar unidad" : "Nueva unidad"}</h2>
                            <button onClick={() => { setOpen(false); setEditingId(null); }}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="fm-label">Número económico *</label><input required value={form.economic_number} onChange={(e) => setForm({ ...form, economic_number: e.target.value })} className="fm-input" data-testid="u-economic" /></div>
                                <div><label className="fm-label">Placas *</label><input required value={form.plates} onChange={(e) => setForm({ ...form, plates: e.target.value })} className="fm-input font-mono" /></div>
                                <div><label className="fm-label">Marca</label><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Modelo</label><input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Año</label><input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })} className="fm-input" /></div>
                                <div><label className="fm-label">Tipo</label><select value={form.ambulance_type} onChange={(e) => setForm({ ...form, ambulance_type: e.target.value })} className="fm-input"><option value="basica">Básica</option><option value="intermedia">Intermedia</option><option value="avanzada">Avanzada</option><option value="neonatal">Neonatal</option></select></div>
                                <div><label className="fm-label">Estatus</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="fm-input"><option value="disponible">Disponible</option><option value="ocupada">Ocupada</option><option value="mantenimiento">Mantenimiento</option><option value="fuera_servicio">Fuera de servicio</option></select></div>
                                <div><label className="fm-label">Base</label><input value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Km actuales</label><input type="number" value={form.current_km} onChange={(e) => setForm({ ...form, current_km: parseInt(e.target.value) || 0 })} className="fm-input" /></div>
                                <div><label className="fm-label">VIN</label><input value={form.vin || ""} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="fm-input font-mono text-xs" /></div>
                                <div><label className="fm-label">Póliza seguro</label><input value={form.insurance_policy || ""} onChange={(e) => setForm({ ...form, insurance_policy: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Vence póliza</label><input type="date" value={form.insurance_expires || ""} onChange={(e) => setForm({ ...form, insurance_expires: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Vence tarjeta circulación</label><input type="date" value={form.circulation_card_expires || ""} onChange={(e) => setForm({ ...form, circulation_card_expires: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Vence verificación</label><input type="date" value={form.verification_expires || ""} onChange={(e) => setForm({ ...form, verification_expires: e.target.value })} className="fm-input" /></div>
                            </div>

                            <div className="border-t border-slate-200 pt-3">
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-2 flex items-center gap-1">
                                    Tripulación a bordo (turno actual)
                                    <HelpTip text="Asigna quién va a bordo de esta unidad hoy. Solo personal con estatus activo aparece en la lista. Puedes cambiarlo cuando rota el turno." />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                        <label className="fm-label">Paramédico a cargo</label>
                                        <select value={form.current_paramedic_id || ""} onChange={(e) => setForm({ ...form, current_paramedic_id: e.target.value })} className="fm-input" data-testid="u-paramedic">
                                            <option value="">— Sin asignar —</option>
                                            {paramedics.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="fm-label">Operador</label>
                                        <select value={form.current_driver_id || ""} onChange={(e) => setForm({ ...form, current_driver_id: e.target.value })} className="fm-input" data-testid="u-driver">
                                            <option value="">— Sin asignar —</option>
                                            {drivers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="fm-label">Médico (si aplica)</label>
                                        <select value={form.current_medic_id || ""} onChange={(e) => setForm({ ...form, current_medic_id: e.target.value })} className="fm-input" data-testid="u-medic">
                                            <option value="">— Sin asignar —</option>
                                            {medics.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div><label className="fm-label">Notas</label><textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="fm-input" /></div>

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent" data-testid="save-unit-btn">{editingId ? "Actualizar" : "Guardar"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
